import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AutomaticRateService } from '../../../services/automatic-rate.service';
import { MainRatesService } from '../../../services/main-rates.service';
import { OthersPayoutsService } from '../../../services/others-payouts.service';
import { PeruPayoutsService } from '../../../services/peru-payouts.service';
import { VitaCalculatorService } from '../../../services/vita-calculator.service';
import { VitaWalletService } from '../../../services/vita-wallet.service';
import {
  CotizacionVM,
  DestinoRemesa,
  OrigenRemesa,
  RielColombia,
} from '../models/remittance.model';
import * as R from '../rules/remittance-rules';
import { tasaVentaParaMontoUsdt } from '../../convert-usdt-ves/convert-usdt-ves.tramos';
import { cotizarVentaClp } from '../../calculator-cryto/clp-usdt.tramos';

/**
 * Punto ÚNICO de cotización del cotizador unificado.
 *
 * Despacha al servicio que corresponde al par (origen, destino) y normaliza
 * la respuesta a `CotizacionVM`. Ningún componente habla con un servicio de
 * tasas directamente, y ningún riel recalcula el envío: todos los números
 * salen del backend.
 *
 * NO toca backend: consume los mismos 10 endpoints que ya usan las pantallas
 * vigentes.
 */
/** Pares del riel propio cuyos límites CLP publica el servidor. */
export type ParRielPropio = 'CLP-VES' | 'VES-CLP';

@Injectable({ providedIn: 'root' })
export class RemittanceQuoteService {
  /**
   * Vida de los datos que se reusan entre cotizaciones. Corto a propósito: son
   * tasas, no catálogos. Nada de esto entra en el camino del envío —
   * `verificarTasa` llama a `cotizar()` directo, sin caché, porque su trabajo
   * es justamente detectar que la tasa se movió.
   */
  private static readonly CACHE_TTL_MS = 60_000;

  /** Sondeos por par: sólo sirven para descubrir mínimo, máximo y tasa. */
  private sondeos = new Map<string, { en: number; c: CotizacionVM }>();

  /**
   * Tasa de referencia VES→CLP. Toda cotización de ese riel arranca pidiendo
   * el MISMO monto (`VES_MONTO_REFERENCIA`) para no gatillar el 500 fuera de
   * tramo: era una llamada fija por tecleo, siempre con la misma respuesta.
   */
  private refVesClp: { en: number; tasa: number; id: number | null } | null = null;

  /**
   * Límites CLP del riel propio que publica el servidor (`limites` de
   * `ultimas-agrupadas`, franjas dinámicas). Se piden UNA vez por par y por
   * ciclo de caché —nunca por tecleo— y un fallo también se cachea como `null`
   * para no reintentar en cada cotización. `null` = mandan las constantes.
   */
  private limitesSrv = new Map<
    ParRielPropio,
    { en: number; lim: R.LimitesPar | null }
  >();
  private limitesEnVuelo = new Map<ParRielPropio, Promise<R.LimitesPar | null>>();

  /**
   * Tramos USDT→VES: se cotiza en cada tecleo (con debounce) y antes se pedían
   * cada vez. Se reusa la última respuesta buena durante `TRAMOS_TTL_MS`, y
   * dos cotizaciones simultáneas comparten la misma llamada. Un 404 (backend
   * sin el endpoint) también se recuerda `TRAMOS_TTL_MS`: no se repite en cada
   * cotización. Cualquier otro fallo no se guarda: la próxima vuelve a preguntar.
   */
  static readonly TRAMOS_TTL_MS = 60_000;
  private tramosUsdtVesCache: { en: number; data: any } | null = null;
  private tramosUsdtVesEnVuelo: Promise<any> | null = null;
  /** Tramos USDT→CLP (22/10/2026): mismo esquema de caché que los de USDT→VES. */
  private tramosUsdtClpCache: { en: number; data: any } | null = null;
  private tramosUsdtClpEnVuelo: Promise<any> | null = null;

  constructor(
    private mainRates: MainRatesService,
    private othersPayouts: OthersPayoutsService,
    private peruPayouts: PeruPayoutsService,
    private automaticRate: AutomaticRateService,
    private vitaWallet: VitaWalletService,
    private vitaCalculator: VitaCalculatorService,
  ) {}

  /**
   * Países Vita realmente disponibles ahora mismo. La grilla de destinos NO
   * puede ser fija: el backend apaga rieles y hay que respetarlo.
   *
   * Se consultan las dos puertas que ya usa el flujo vigente:
   *   1. `getServiceStatus()` → flag global `VITA_SERVICIO`. Si está apagado,
   *      no hay ningún destino Vita.
   *   2. `listCountries()` → devuelve SÓLO los países activos. Si el backend
   *      desactiva Colombia, no viene en la lista.
   *
   * La tercera puerta —`activo` por riel origen→destino— viaja en cada
   * cotización y la aplica `vitaVM`.
   *
   * Ante error devuelve un conjunto VACÍO: cerrado por defecto. Es preferible
   * ocultar un destino disponible a ofrecer uno que el backend va a rechazar.
   * `fallo` distingue ese caso del conjunto vacío legítimo —Vita apagado— para
   * que la UI pueda ofrecer un reintento en vez de quedarse a medias.
   */
  /**
   * Límites CLP del par que ya se conocen, sin preguntar. `null` si todavía
   * no se consultaron o el servidor no los publica: el llamador cae a las
   * constantes de siempre.
   */
  limitesClpConocidos(par: ParRielPropio): R.LimitesPar | null {
    return this.limitesSrv.get(par)?.lim ?? null;
  }

  /**
   * Límites CLP del par publicados por el servidor, cacheados. Nunca lanza:
   * ante cualquier problema devuelve `null` y el cotizador sigue como antes.
   */
  async limitesClpServidor(par: ParRielPropio): Promise<R.LimitesPar | null> {
    const ahora = Date.now();
    const previo = this.limitesSrv.get(par);
    if (previo && ahora - previo.en < RemittanceQuoteService.CACHE_TTL_MS) {
      return previo.lim;
    }
    const enVuelo = this.limitesEnVuelo.get(par);
    if (enVuelo) return enVuelo;

    const p = (async () => {
      let lim: R.LimitesPar | null = null;
      try {
        const obs =
          par === 'CLP-VES'
            ? this.mainRates.obtenerTasasPublicas()
            : this.mainRates.obtenerTasasVesClpPublicas();
        const res: any = await firstValueFrom(obs);
        lim =
          R.limitesClpDesdeRespuesta(res?.data) ?? R.limitesClpDesdeRespuesta(res);
      } catch {
        lim = null;
      }
      this.limitesSrv.set(par, { en: Date.now(), lim });
      return lim;
    })();
    this.limitesEnVuelo.set(par, p);
    try {
      return await p;
    } finally {
      this.limitesEnVuelo.delete(par);
    }
  }

  async destinosVitaDisponibles(): Promise<{
    activos: Set<string>;
    fallo: boolean;
  }> {
    try {
      // Las dos puertas se consultan EN PARALELO: encadenadas, media grilla
      // esperaba dos viajes de red antes de aparecer. Cuando Vita está apagado
      // se gasta una consulta de más, que es el caso poco frecuente.
      const [estado, paises] = await Promise.all([
        firstValueFrom(this.vitaWallet.getServiceStatus()),
        firstValueFrom(this.vitaCalculator.listCountries()).catch(() => null),
      ]);

      if (!estado?.enabled) return { activos: new Set(), fallo: false };
      if (!paises) return { activos: new Set(), fallo: true };

      return {
        activos: new Set(paises.map((p) => (p.code || '').toUpperCase())),
        fallo: false,
      };
    } catch {
      return { activos: new Set(), fallo: true };
    }
  }

  /**
   * Cotiza el par y sella el instante en que la respuesta llegó.
   *
   * El sello se pone acá, en el único embudo por el que pasan todas las
   * cotizaciones, y no en cada riel: así ninguna implementación nueva se puede
   * olvidar de ponerlo. Sirve para reportar cuándo el usuario vio el número
   * que se le honró — el snapshot marca el confirmar, no el mostrar.
   */
  async cotizar(
    origen: OrigenRemesa,
    destino: DestinoRemesa,
    monto: number,
    rielCo: RielColombia = 'COBREB',
  ): Promise<CotizacionVM> {
    const c = await this.resolverCotizacion(origen, destino, monto, rielCo);
    return { ...c, cotizadoEn: Date.now() };
  }

  /**
   * Despacho por riel. Lanza sólo si el riel es desconocido; cualquier fallo
   * de red se traduce a una `CotizacionVM` con `disponible: false` para que la
   * UI muestre un motivo en vez de romperse.
   */
  private async resolverCotizacion(
    origen: OrigenRemesa,
    destino: DestinoRemesa,
    monto: number,
    rielCo: RielColombia = 'COBREB',
  ): Promise<CotizacionVM> {
    if (!(monto > 0)) {
      return this.noDisponible(origen, destino, monto, 'Ingresa un monto.');
    }

    try {
      if (destino === 'VE') {
        return origen === 'USDT'
          ? await this.usdtVes(monto)
          : await this.clpVes(monto);
      }
      if (destino === 'CL') {
        return origen === 'USDT'
          ? await this.usdtClp(monto)
          : await this.vesClp(monto);
      }
      if (destino === 'CO') return await this.colombia(origen, monto, rielCo);
      if (destino === 'PE') return await this.peru(origen, monto);
    } catch (e: any) {
      const msg =
        typeof e === 'string' ? e : 'No pudimos calcular el envío. Intenta de nuevo.';
      return this.noDisponible(origen, destino, monto, msg);
    }

    return this.noDisponible(origen, destino, monto, 'Destino no disponible.');
  }

  /**
   * SONDEO: cotización con un monto seguro, sólo para descubrir el mínimo, el
   * máximo y la tasa del par. No se muestra.
   *
   * Se cachea por par: cambiar entre Colombia y Perú, o volver a un país ya
   * visitado, volvía a preguntar exactamente lo mismo. Un sondeo que falló no
   * se cachea — el próximo intento tiene que volver a preguntar.
   *
   * NUNCA está en el camino del envío: `verificarTasa` usa `cotizar()`.
   */
  async sondear(
    origen: OrigenRemesa,
    destino: DestinoRemesa,
    rielCo: RielColombia = 'COBREB',
  ): Promise<CotizacionVM> {
    const clave = `${origen}|${destino}|${rielCo}`;
    const ahora = Date.now();
    const previo = this.sondeos.get(clave);
    if (previo && ahora - previo.en < RemittanceQuoteService.CACHE_TTL_MS) {
      return previo.c;
    }

    const c = await this.cotizar(
      origen,
      destino,
      R.montoSondeo(origen, destino),
      rielCo,
    );
    if (c.disponible) this.sondeos.set(clave, { en: ahora, c });
    return c;
  }

  /**
   * COTIZACIÓN INVERSA: cuánto hay que pagar para que el beneficiario reciba
   * un monto dado.
   *
   * No existe endpoint inverso en ningún riel, y no se toca backend. Se resuelve
   * buscando el monto de origen con las cotizaciones que ya existen:
   *
   *   1. punto de partida — la cotización que la pantalla ya tiene, o un sondeo
   *      si no hay ninguna todavía;
   *   2. primera estimación — `objetivo ÷ tasaEfectiva`, exacta en los rieles
   *      propios (donde la tasa es un multiplicador limpio);
   *   3. un paso de corrección sobre la tasa MARGINAL (`tasaReferencia`) si el
   *      recibe no cayó dentro de la tolerancia. Hace falta en los rieles Vita,
   *      donde la comisión no escala, y en CLP↔VES, donde la tasa cambia de
   *      tramo con el monto.
   *
   * Tope de tres cotizaciones. Lo que se ajusta es el monto de ORIGEN: el
   * `recibe` que se devuelve es siempre el que calculó el backend, nunca la
   * estimación del front ni el número que tipeó el usuario.
   */
  async cotizarPorDestino(
    origen: OrigenRemesa,
    destino: DestinoRemesa,
    objetivo: number,
    rielCo: RielColombia = 'COBREB',
    base: CotizacionVM | null = null,
  ): Promise<CotizacionVM> {
    if (!(objetivo > 0)) {
      return this.noDisponible(origen, destino, 0, 'Ingresa cuánto quieres que reciba.');
    }

    const partida = this.sirveDePartida(base, origen, destino)
      ? (base as CotizacionVM)
      : await this.sondear(origen, destino, rielCo);

    // Sin tasa no hay inverso posible: se devuelve el motivo del sondeo.
    if (!partida.tasaEfectiva) return partida;

    const estimado = R.invertirLocal(objetivo, partida.tasaEfectiva);
    if (estimado == null) return partida;

    let mejor = await this.cotizar(
      origen,
      destino,
      R.redondearOrigen(origen, estimado),
      rielCo,
    );

    if (mejor.disponible && R.necesitaRefinar(objetivo, mejor.recibe)) {
      const siguiente = R.siguienteEstimacion(
        objetivo,
        mejor.monto,
        mejor.recibe,
        mejor.tasaReferencia ?? mejor.tasaEfectiva,
      );
      if (siguiente != null) {
        const corregida = await this.cotizar(
          origen,
          destino,
          R.redondearOrigen(origen, siguiente),
          rielCo,
        );
        mejor = this.masCercana(objetivo, mejor, corregida);
      }
    }

    return mejor;
  }

  /** La cotización que la pantalla ya tiene sirve como punto de partida. */
  private sirveDePartida(
    c: CotizacionVM | null,
    origen: OrigenRemesa,
    destino: DestinoRemesa,
  ): boolean {
    return (
      !!c && c.origen === origen && c.destino === destino && !!c.tasaEfectiva
    );
  }

  /**
   * De dos cotizaciones válidas, la que deja al beneficiario más cerca del
   * objetivo. Protege del paso de corrección que se pasa de largo: nunca se
   * devuelve algo peor que lo que ya se tenía.
   */
  private masCercana(
    objetivo: number,
    a: CotizacionVM,
    b: CotizacionVM,
  ): CotizacionVM {
    if (!b.disponible || b.recibe == null) return a;
    if (!a.disponible || a.recibe == null) return b;
    return Math.abs(b.recibe - objetivo) < Math.abs(a.recibe - objetivo) ? b : a;
  }

  // ───────────────────── Rieles propios ─────────────────────

  /** CLP → VES. El backend devuelve un multiplicador; el front multiplica. */
  private async clpVes(monto: number): Promise<CotizacionVM> {
    const lim = R.limitesDe(
      'CLP',
      'VE',
      undefined,
      await this.limitesClpServidor('CLP-VES'),
    );

    // Bajo el mínimo comercial (15.000 CLP) el backend `obtenerTasaPorMonto`
    // responde error —el monto cae fuera de tramo— y el usuario terminaba
    // viendo el genérico "No pudimos calcular el envío. Intentá de nuevo.".
    // Cortamos antes de pedir la tasa y devolvemos una cotización `bajoMinimo`
    // para que la UI muestre el aviso coherente ("El mínimo para este destino
    // es 15.000 CLP"). `disponible: true` es a propósito: el getter `bloqueo`
    // chequea `disponible` antes que `bajoMinimo`, así que con `false` se
    // mostraría el motivo genérico en vez del mínimo.
    if (monto < (lim.min ?? 0)) {
      return {
        origen: 'CLP',
        destino: 'VE',
        monedaOrigen: 'CLP',
        monedaDestino: 'VES',
        monto,
        recibe: null,
        tasaEfectiva: null,
        tasaReferencia: null,
        comision: null,
        sinComision: true,
        min: lim.min,
        max: lim.max,
        bajoMinimo: true,
        excedeMaximo: false,
        disponible: true,
        activo: true,
        motivo: null,
        businessDays: null,
        etaLabel: R.etaDe('VE', null),
        decimales: R.decimalesDe('VE'),
        tasaCambioId: null,
        valorCambioId: null,
      };
    }

    const r = await firstValueFrom(
      this.mainRates.obtenerTasaPorMonto(monto, 'Client'),
    );
    const tasa = r?.tasaClient || 0;

    return {
      origen: 'CLP',
      destino: 'VE',
      monedaOrigen: 'CLP',
      monedaDestino: 'VES',
      monto,
      recibe: tasa > 0 ? monto * tasa : null,
      tasaEfectiva: tasa || null,
      tasaReferencia: null, // el riel propio no publica referencia
      comision: null,
      sinComision: true,
      min: lim.min,
      max: lim.max,
      bajoMinimo: monto < (lim.min ?? 0),
      excedeMaximo: lim.max != null && monto > lim.max,
      disponible: tasa > 0,
      activo: true,
      motivo: tasa > 0 ? null : 'No hay tasa disponible en este momento.',
      businessDays: null,
      etaLabel: R.etaDe('VE', null),
      decimales: R.decimalesDe('VE'),
      tasaCambioId: r?.id ?? null,
      valorCambioId: r?.id ?? null,
    };
  }

  /**
   * VES → CLP. Dos consultas, y el orden importa.
   *
   * `rates-public/ves-clp/por-monto` resuelve el tramo convirtiendo VES→CLP y
   * responde **500** si el monto cae fuera de los tramos. Por eso nunca se lo
   * consulta con el monto crudo del usuario:
   *   1. tasa con un monto de REFERENCIA seguro,
   *   2. con esa tasa se derivan mínimo y máximo en VES,
   *   3. sólo si el monto cae dentro del rango se re-consulta con el real,
   *      para tomar la tasa del tramo que corresponde.
   */
  private async vesClp(monto: number): Promise<CotizacionVM> {
    const [ref, clpSrv] = await Promise.all([
      this.tasaReferenciaVesClp(),
      this.limitesClpServidor('VES-CLP'),
    ]);
    let tasaVesPorClp = ref.tasa;
    let tasaId: number | null = ref.id;

    const lim = R.limitesVesDesdeTasa(tasaVesPorClp, clpSrv);
    const dentro =
      lim.min != null && lim.max != null && monto >= lim.min && monto <= lim.max;

    // Con el monto de referencia la tasa del tramo ya es la que se tiene: pedir
    // otra vez lo mismo era la llamada más repetida de todo el cotizador.
    if (dentro && monto !== R.VES_MONTO_REFERENCIA) {
      try {
        const exacta = await firstValueFrom(
          this.mainRates.obtenerTasaVesClpPorMonto(monto, 'Client'),
        );
        if (exacta?.tasaAplicada) {
          tasaVesPorClp = exacta.tasaAplicada;
          tasaId = exacta.id ?? tasaId;
        }
      } catch {
        // Nos quedamos con la de referencia: mejor una tasa aproximada
        // visible que un error en pantalla.
      }
    }

    const ok = tasaVesPorClp > 0;
    // Recalculados con la tasa final, que puede ser la del tramo.
    const limFinal = R.limitesVesDesdeTasa(tasaVesPorClp, clpSrv);

    return {
      origen: 'VES',
      destino: 'CL',
      monedaOrigen: 'VES',
      monedaDestino: 'CLP',
      monto,
      // `tasaAplicada` es VES por 1 CLP → para obtener CLP se DIVIDE.
      recibe: ok ? monto / tasaVesPorClp : null,
      tasaEfectiva: ok ? 1 / tasaVesPorClp : null,
      tasaReferencia: null,
      comision: null,
      sinComision: true,
      min: limFinal.min,
      max: limFinal.max,
      bajoMinimo: ok && limFinal.min != null && monto < limFinal.min,
      excedeMaximo: ok && limFinal.max != null && monto > limFinal.max,
      disponible: ok,
      activo: true,
      motivo: ok ? null : 'No hay tasa disponible en este momento.',
      businessDays: null,
      etaLabel: R.etaDe('CL', null),
      decimales: R.decimalesDe('CL'),
      tasaCambioId: tasaId,
      valorCambioId: tasaId,
    };
  }

  /**
   * Tasa VES→CLP del monto de REFERENCIA, cacheada.
   *
   * No es la tasa del envío: es la que permite derivar mínimo y máximo en VES
   * sin gatillar el 500 del endpoint fuera de tramo. La del envío sale de la
   * consulta con el monto real, que sigue siendo siempre fresca — por eso
   * cachear ésta no afecta la detección de cambio de tasa.
   */
  private async tasaReferenciaVesClp(): Promise<{
    tasa: number;
    id: number | null;
  }> {
    const ahora = Date.now();
    const previo = this.refVesClp;
    if (previo && ahora - previo.en < RemittanceQuoteService.CACHE_TTL_MS) {
      return { tasa: previo.tasa, id: previo.id };
    }

    const r = await firstValueFrom(
      this.mainRates.obtenerTasaVesClpPorMonto(R.VES_MONTO_REFERENCIA, 'Client'),
    );
    const tasa = r?.tasaAplicada || 0;
    const id = r?.id ?? null;
    if (tasa > 0) this.refVesClp = { en: ahora, tasa, id };
    return { tasa, id };
  }

  /**
   * USDT → VES (con beneficiario). Mínimo propio: 10 USDT.
   *
   * Con tramos USDT→VES (21/10/2026) se cotiza la tasa del tramo del monto;
   * si los tramos fallan o no hay, la base de siempre (el back acepta las dos).
   */
  private async usdtVes(monto: number): Promise<CotizacionVM> {
    const [r, tramos]: [any, any] = await Promise.all([
      firstValueFrom(this.mainRates.getUsdtVesRateForUser()),
      this.tramosUsdtVes(),
    ]);
    const base = r?.success ? r?.data?.tasaFinal || 0 : 0;
    const tasa = base > 0 ? tasaVentaParaMontoUsdt(tramos, monto, base).tasa : 0;
    const lim = R.limitesDe('USDT', 'VE');

    return {
      origen: 'USDT',
      destino: 'VE',
      monedaOrigen: 'USDT',
      monedaDestino: 'VES',
      monto,
      recibe: tasa > 0 ? monto * tasa : null,
      tasaEfectiva: tasa || null,
      tasaReferencia: null,
      comision: null,
      sinComision: true,
      min: lim.min,
      max: lim.max,
      bajoMinimo: monto < (lim.min ?? 0),
      excedeMaximo: false,
      disponible: tasa > 0,
      activo: true,
      motivo: tasa > 0 ? null : 'No hay tasa USDT disponible en este momento.',
      businessDays: null,
      etaLabel: R.etaDe('VE', null),
      decimales: R.decimalesDe('VE'),
    };
  }

  /**
   * Tramos USDT→VES; null si fallan (se cotiza la base). Nunca lanza.
   * Cacheados `TRAMOS_TTL_MS` (ver `tramosUsdtVesCache`).
   */
  private async tramosUsdtVes(): Promise<any> {
    const c = this.tramosUsdtVesCache;
    if (c && Date.now() - c.en < RemittanceQuoteService.TRAMOS_TTL_MS) return c.data;
    if (this.tramosUsdtVesEnVuelo) return this.tramosUsdtVesEnVuelo;

    const enVuelo = (async () => {
      try {
        const data = await firstValueFrom(this.mainRates.obtenerTramosUsdtVes());
        this.tramosUsdtVesCache = { en: Date.now(), data };
        return data;
      } catch (e) {
        if (esNoEncontrado(e)) this.tramosUsdtVesCache = { en: Date.now(), data: null };
        return null;
      }
    })();
    this.tramosUsdtVesEnVuelo = enVuelo;
    enVuelo.then(() => {
      if (this.tramosUsdtVesEnVuelo === enVuelo) this.tramosUsdtVesEnVuelo = null;
    });
    return enVuelo;
  }

  /**
   * USDT → CLP. Sólo es remesa para el usuario VE (retiro a banco chileno);
   * para el usuario CL es una conversión y no se ofrece en este cotizador.
   * Mínimo 20 USDT — y el flujo destino exige además saldo ≥ mínimo.
   */
  private async usdtClp(monto: number): Promise<CotizacionVM> {
    // Con tramos USDT→CLP (22/10/2026) se cotiza la tasa del tramo del monto;
    // si los tramos fallan o no hay, la base de siempre (el back acepta las dos
    // con ±1 % y liquida con la del tramo).
    const [r, tramos]: [any, any] = await Promise.all([
      this.automaticRate.getUsdtRateSellForUser(),
      this.tramosUsdtClp(),
    ]);
    const base = Number(r?.valoresCambio?.tasaUsdtClp) || 0;
    const tasa = base > 0 && tramos ? cotizarVentaClp({ data: tramos, montoUsdt: monto, base }).tasa : base;
    const lim = R.limitesDe('USDT', 'CL');

    return {
      origen: 'USDT',
      destino: 'CL',
      monedaOrigen: 'USDT',
      monedaDestino: 'CLP',
      monto,
      recibe: tasa > 0 ? monto * tasa : null,
      tasaEfectiva: tasa || null,
      tasaReferencia: null,
      comision: null,
      sinComision: true,
      min: lim.min,
      max: lim.max,
      bajoMinimo: monto < (lim.min ?? 0),
      excedeMaximo: false,
      disponible: tasa > 0,
      activo: true,
      motivo: tasa > 0 ? null : 'No hay tasa USDT disponible en este momento.',
      businessDays: null,
      etaLabel: R.etaDe('CL', null),
      decimales: R.decimalesDe('CL'),
      tasaCambioId: r?.valoresCambio?.tasaCambioId ?? null,
      valorCambioId: r?.valoresCambio?.valorCambioId ?? null,
    };
  }

  /**
   * Tramos USDT→CLP; null si fallan o el servicio no los tiene (se cotiza la
   * base). Nunca lanza. Cacheados `TRAMOS_TTL_MS`.
   */
  private async tramosUsdtClp(): Promise<any> {
    const c = this.tramosUsdtClpCache;
    if (c && Date.now() - c.en < RemittanceQuoteService.TRAMOS_TTL_MS) return c.data;
    if (this.tramosUsdtClpEnVuelo) return this.tramosUsdtClpEnVuelo;

    const enVuelo = (async () => {
      try {
        const data = await firstValueFrom(this.mainRates.obtenerTramosUsdtClp());
        this.tramosUsdtClpCache = { en: Date.now(), data };
        return data;
      } catch (e) {
        if (esNoEncontrado(e)) this.tramosUsdtClpCache = { en: Date.now(), data: null };
        return null;
      }
    })();
    this.tramosUsdtClpEnVuelo = enVuelo;
    enVuelo.then(() => {
      if (this.tramosUsdtClpEnVuelo === enVuelo) this.tramosUsdtClpEnVuelo = null;
    });
    return enVuelo;
  }

  // ───────────────────── Rieles Vita ─────────────────────

  private async colombia(
    origen: OrigenRemesa,
    monto: number,
    riel: RielColombia,
  ): Promise<CotizacionVM> {
    if (origen === 'CLP') {
      const c = await firstValueFrom(this.othersPayouts.cotizarClp(riel, monto));
      return this.vitaVM('CLP', 'CO', monto, riel, {
        recibe: c.recibeCop,
        tasa: c.tasaCopClp,
        referencia: c.tasaReferenciaCopClp,
        min: c.minClp,
        max: c.maxClp,
        raw: c,
      });
    }
    if (origen === 'VES') {
      const c = await firstValueFrom(this.othersPayouts.cotizarVes(riel, monto));
      return this.vitaVM('VES', 'CO', monto, riel, {
        recibe: c.recibeCop,
        tasa: c.tasaCopVes,
        referencia: c.tasaReferenciaCopVes,
        min: c.minVes,
        max: c.maxVes,
        raw: c,
      });
    }
    const c = await firstValueFrom(this.othersPayouts.cotizar(riel, monto));
    return this.vitaVM('USDT', 'CO', monto, riel, {
      recibe: c.recibeCop,
      tasa: c.tasaCop,
      referencia: c.tasaReferenciaCop,
      min: null,
      max: null,
      raw: c,
    });
  }

  private async peru(origen: OrigenRemesa, monto: number): Promise<CotizacionVM> {
    if (origen === 'CLP') {
      const c = await firstValueFrom(this.peruPayouts.cotizarClp(monto));
      return this.vitaVM('CLP', 'PE', monto, null, {
        recibe: c.recibePen,
        tasa: c.tasaPenClp,
        referencia: c.tasaReferenciaPenClp,
        min: c.minClp,
        max: c.maxClp,
        raw: c,
      });
    }
    if (origen === 'VES') {
      const c = await firstValueFrom(this.peruPayouts.cotizarVes(monto));
      return this.vitaVM('VES', 'PE', monto, null, {
        recibe: c.recibePen,
        tasa: c.tasaPenVes,
        referencia: c.tasaReferenciaPenVes,
        min: c.minVes,
        max: c.maxVes,
        raw: c,
      });
    }
    const c = await firstValueFrom(this.peruPayouts.cotizar(monto));
    return this.vitaVM('USDT', 'PE', monto, null, {
      recibe: c.recibePen,
      tasa: c.tasaPen,
      referencia: c.tasaReferenciaPen,
      min: null,
      max: null,
      raw: c,
    });
  }

  /**
   * Normaliza cualquiera de las seis cotizaciones Vita.
   *
   * OJO con la asimetría del backend: los contratos de origen USDT
   * (`CotizacionCo` / `CotizacionPe`) NO traen `min`, `max`, `activo`,
   * `motivo`, `bajoMinimo` ni `excedeMaximo`. Los de CLP y VES sí. Por eso
   * los límites se resuelven con `limitesDe`, que suple lo que falta.
   */
  private vitaVM(
    origen: OrigenRemesa,
    destino: DestinoRemesa,
    monto: number,
    riel: RielColombia | null,
    d: {
      recibe: number | null | undefined;
      tasa: number | null | undefined;
      referencia: number | null | undefined;
      min: number | null | undefined;
      max: number | null | undefined;
      raw: any;
    },
  ): CotizacionVM {
    const c = d.raw || {};
    const lim = R.limitesDe(origen, destino, {
      min: d.min ?? null,
      max: d.max ?? null,
    });

    // Con USDT el backend no manda las banderas: se derivan de los límites.
    const bajoMinimo =
      c.bajoMinimo === true || (lim.min != null && monto < lim.min);
    const excedeMaximo =
      c.excedeMaximo === true || (lim.max != null && monto > lim.max);

    return {
      origen,
      destino,
      monedaOrigen: origen,
      monedaDestino: R.DESTINOS[destino].moneda,
      monto,
      recibe: d.recibe ?? null,
      tasaEfectiva: d.tasa ?? null,
      tasaReferencia: d.referencia ?? null,
      // Con la tasa que se MUESTRA (referencia si la hay, si no la efectiva),
      // no con la exacta: es lo que hace que `subtotal − comisión = recibe`
      // cierre en pantalla. Ver `comisionEnDestino`.
      comision: R.comisionEnDestino(
        monto,
        d.recibe,
        d.referencia ?? d.tasa,
        origen,
        destino,
      ),
      sinComision: false,
      min: lim.min,
      max: lim.max,
      bajoMinimo,
      excedeMaximo,
      disponible: c.disponible === true,
      activo: c.activo !== false,
      motivo: R.motivoLegible(c.motivo),
      businessDays: c.businessDays ?? null,
      etaLabel: R.etaDe(destino, riel),
      decimales: R.decimalesDe(destino),
    };
  }

  // ───────────────────── Helpers ─────────────────────

  private noDisponible(
    origen: OrigenRemesa,
    destino: DestinoRemesa,
    monto: number,
    motivo: string,
  ): CotizacionVM {
    const lim = R.limitesDe(
      origen,
      destino,
      undefined,
      origen === 'CLP' && destino === 'VE'
        ? this.limitesClpConocidos('CLP-VES')
        : null,
    );
    return {
      origen,
      destino,
      monedaOrigen: origen,
      monedaDestino: R.DESTINOS[destino]?.moneda ?? '',
      monto,
      recibe: null,
      tasaEfectiva: null,
      tasaReferencia: null,
      comision: null,
      sinComision: R.esRielPropio(destino),
      min: lim.min,
      max: lim.max,
      bajoMinimo: false,
      excedeMaximo: false,
      disponible: false,
      activo: false,
      motivo,
      businessDays: null,
      etaLabel: '',
      decimales: R.decimalesDe(destino),
    };
  }
}

/**
 * 404 del GET de tramos (el backend no tiene el endpoint): llega crudo
 * (`HttpErrorResponse`) o envuelto por `MainRatesService.handleError`
 * (`{ originalError }`).
 */
function esNoEncontrado(e: any): boolean {
  return (e?.status ?? e?.originalError?.status) === 404;
}
